import { useEffect, useRef, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import TrialBanner from "@/components/TrialBanner";
import SubscriptionExpiredGate from "@/components/SubscriptionExpiredGate";

// Landing page — loaded immediately (it IS the first screen every visitor sees)
import FinancialLiteracyPage from "@/pages/FinancialLiteracyPage";
import ZainPage from "@/pages/ZainPage";
// AuthCallback handles a URL hash synchronously at the top of AppRouter, so it
// must be a direct import (lazy loads fire after render, which is too late).
import AuthCallback from "@/pages/AuthCallback";

// All other pages are lazy-loaded — they ship in separate chunks and are only
// downloaded when a user actually navigates to that route.
const EntrepreneurshipWorkshopPage = lazy(() => import("@/pages/EntrepreneurshipWorkshopPage"));
const ForSchoolsPage = lazy(() => import("@/pages/ForSchoolsPage"));
const RoleSelection = lazy(() => import("@/pages/RoleSelection"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const WalletPage = lazy(() => import("@/pages/WalletPage"));
const MyWalletPage = lazy(() => import("@/pages/MyWalletPage"));
const StorePage = lazy(() => import("@/pages/StorePage"));
const QuestsPage = lazy(() => import("@/pages/QuestsPage"));
const AchievementsPage = lazy(() => import("@/pages/AchievementsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const LearnPage = lazy(() => import("@/pages/LearnPage"));
const TopicPage = lazy(() => import("@/pages/TopicPage"));
const LessonPage = lazy(() => import("@/pages/LessonPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const TeacherDashboard = lazy(() => import("@/pages/TeacherDashboard"));
const ParentDashboard = lazy(() => import("@/pages/ParentDashboard"));
const ContentManagement = lazy(() => import("@/pages/ContentManagement"));
const LiveClassesPage = lazy(() => import("@/pages/LiveClassesPage"));
const LiveClassesAdmin = lazy(() => import("@/pages/LiveClassesAdmin"));
const AdminStoreManagement = lazy(() => import("@/pages/AdminStoreManagement"));
const AdminInvestmentManagement = lazy(() => import("@/pages/AdminInvestmentManagement"));
const ClassmatesPage = lazy(() => import("@/pages/ClassmatesPage"));
const SavingsGoalsPage = lazy(() => import("@/pages/SavingsGoalsPage"));
const AdminQuestsPage = lazy(() => import("@/pages/AdminQuestsPage"));
const ParentShoppingList = lazy(() => import("@/pages/ParentShoppingList"));
const MoneyGardenPage = lazy(() => import("@/pages/MoneyGardenPage"));
const AdminGardenManagement = lazy(() => import("@/pages/AdminGardenManagement"));
const StockMarketPage = lazy(() => import("@/pages/StockMarketPage"));
const AdminStockManagement = lazy(() => import("@/pages/AdminStockManagement"));
const SchoolLogin = lazy(() => import("@/pages/SchoolLogin"));
const SchoolDashboard = lazy(() => import("@/pages/SchoolDashboard"));
const GiftingPage = lazy(() => import("@/pages/GiftingPage"));
const AdminBadgeManagement = lazy(() => import("@/pages/AdminBadgeManagement"));
const AdminVideoManagement = lazy(() => import("@/pages/AdminVideoManagement"));
const LendingBorrowingPage = lazy(() => import("@/pages/LendingBorrowingPage"));
const AdminGlossaryManagement = lazy(() => import("@/pages/AdminGlossaryManagement"));
const AdminTeacherRepository = lazy(() => import("@/pages/AdminTeacherRepository"));
const GlossaryPage = lazy(() => import("@/pages/GlossaryPage"));
const MyJobsPage = lazy(() => import("@/pages/MyJobsPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const CompleteSignupPage = lazy(() => import("@/pages/CompleteSignupPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const RefundsPage = lazy(() => import("@/pages/RefundsPage"));
const ActivityScoresPage = lazy(() => import("@/pages/ActivityScoresPage"));
const TeacherActivityAnalytics = lazy(() => import("@/pages/TeacherActivityAnalytics"));
const AdminSubscriptionManagement = lazy(() => import("@/pages/AdminSubscriptionManagement"));


import ContentProtection from "@/components/ContentProtection";
import { useUploadProgress, UploadProgressBar } from "@/components/UploadProgress";

// Components
import OnboardingTour from "@/components/OnboardingTour";
import ChildQuickNav from "@/components/ChildQuickNav";

// Minimal spinner shown while a lazy chunk is being fetched
const PageLoader = () => (
  <div className="min-h-screen bg-[#E0FBFC] flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-[#1D3557] border-t-[#FFD23F] rounded-full animate-spin" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper to build full URL for uploaded assets (thumbnails, pdfs, etc.)
export const getAssetUrl = (path) => {
  if (!path) return null;
  // If already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // If it's a relative path starting with /api/uploads, prepend backend URL
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  // Otherwise, assume it's a path needing the full uploads prefix
  return `${BACKEND_URL}/api/uploads/${path}`;
};

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add axios interceptor to include session token in all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle session invalidation (single device login)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // A 401 from a login/auth attempt is an expected failure (e.g. wrong
      // password) that the auth page handles itself — do NOT treat it as an
      // expired session or redirect away, or the user never sees the error.
      const reqUrl = error.config?.url || '';
      const isAuthAttempt = /\/api\/auth\/(login|admin-login|school-login|signup|register)/.test(reqUrl);
      if (isAuthAttempt) {
        return Promise.reject(error);
      }
      // Session is invalid - user may have logged in on another device
      const currentPath = window.location.pathname;
      // Don't redirect if already on auth pages
      if (currentPath !== '/' && currentPath !== '/auth' && currentPath !== '/auth/callback' && currentPath !== '/login' && currentPath !== '/register') {
        localStorage.removeItem('session_token');
        // Show message and redirect
        window.location.href = '/?session_expired=true';
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasChecked = useRef(false);
  
  useEffect(() => {
    // If user data was passed from AuthCallback, use it directly
    if (location.state?.user && !hasChecked.current) {
      console.log('Using user from location state:', location.state.user.email);
      setUser(location.state.user);
      setIsAuthenticated(true);
      hasChecked.current = true;
      return;
    }
    
    // Skip if we already have a valid user
    if (isAuthenticated === true && user) {
      return;
    }
    
    const checkAuth = async () => {
      try {
        console.log('Checking auth via /api/auth/me...');
        
        // Configure request with both cookie and header-based auth
        const config = { withCredentials: true };
        
        // Also try Authorization header with stored token as fallback
        const storedToken = localStorage.getItem('session_token');
        if (storedToken) {
          config.headers = { Authorization: `Bearer ${storedToken}` };
        }
        
        const response = await axios.get(`${API}/auth/me`, config);
        console.log('Auth check success:', response.data.email);
        setUser(response.data);
        setIsAuthenticated(true);
        hasChecked.current = true;
        
        // If user has no role, redirect to role selection
        if (!response.data.role && location.pathname !== '/role-selection') {
          navigate('/role-selection', { state: { user: response.data } });
        }
      } catch (error) {
        console.log('Auth check failed:', error.response?.status, error.response?.data?.detail);
        // Clear any stored token on auth failure
        localStorage.removeItem('session_token');
        setIsAuthenticated(false);
        navigate('/');
      }
    };
    
    if (!hasChecked.current) {
      checkAuth();
    }
  }, [navigate, location.state, location.pathname, isAuthenticated, user]);
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#E0FBFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#1D3557] border-t-[#FFD23F] rounded-full animate-spin"></div>
          <p className="text-xl font-bold text-[#1D3557]">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return null;
  }
  
  // Clone children with user prop and include OnboardingTour for child/parent
  return (
    <>
      <TrialBanner user={user} />
      <SubscriptionExpiredGate
        user={user}
        onLogout={async () => {
          try {
            await axios.post(`${API}/auth/logout`);
          } catch {
            /* ignore logout errors */
          }
          setUser(null);
          window.location.href = '/login';
        }}
      />
      <OnboardingTour user={user} onComplete={() => setUser({...user, has_completed_onboarding: true})} />
      {children({ user, setUser })}
      {user?.role === 'child' && <ChildQuickNav />}
    </>
  );
};

// Scrolls the window to the top whenever the route path changes, so
// navigating to a new page (e.g. clicking a product card link) always
// starts at that page's hero instead of preserving the previous scroll
// position. Anchor-only navigation (e.g. href="#programs") does not change
// the pathname, so it is unaffected.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render (not in useEffect)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/" element={<FinancialLiteracyPage />} />
      <Route path="/financial-literacy" element={<Navigate to="/" replace />} />
      <Route path="/entrepreneurship-workshop" element={<EntrepreneurshipWorkshopPage />} />
      <Route path="/for-schools" element={<ForSchoolsPage />} />
      <Route path="/zain" element={<ZainPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/signup" element={<Navigate to="/register" replace />} />
      <Route path="/complete-signup" element={<CompleteSignupPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refunds" element={<RefundsPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Fallback: Handle if Google OAuth callback hits frontend without /api prefix */}
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      <Route path="/school-login" element={<SchoolLogin />} />
      <Route path="/school-dashboard" element={<SchoolDashboard />} />
      <Route path="/role-selection" element={
        <ProtectedRoute>
          {({ user, setUser }) => <RoleSelection user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          {({ user }) => <WalletPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/store" element={
        <ProtectedRoute>
          {({ user }) => <StorePage user={user} />}
        </ProtectedRoute>
      } />
      {/* Redirect /investments to /stock-market */}
      <Route path="/investments" element={<Navigate to="/stock-market" replace />} />
      <Route path="/garden" element={
        <ProtectedRoute>
          {({ user }) => <MoneyGardenPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/quests" element={
        <ProtectedRoute>
          {({ user }) => <QuestsPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          {({ user }) => <AchievementsPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/my-jobs" element={
        <ProtectedRoute>
          {({ user }) => <MyJobsPage user={user} />}
        </ProtectedRoute>
      } />
      {/* AI Buddy chat removed to prevent children from using AI for test answers */}
      <Route path="/lending" element={
        <ProtectedRoute>
          {({ user }) => <LendingBorrowingPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          {({ user, setUser }) => <ProfilePage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/learn" element={
        <ProtectedRoute>
          {({ user }) => <LearnPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/learn/topic/:topicId" element={
        <ProtectedRoute>
          {({ user }) => <TopicPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/learn/lesson/:lessonId" element={
        <ProtectedRoute>
          {({ user }) => <LessonPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          {({ user, setUser }) => <AdminPage user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/teacher-dashboard" element={
        <ProtectedRoute>
          {({ user }) => <TeacherDashboard user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/parent-dashboard" element={
        <ProtectedRoute>
          {({ user }) => <ParentDashboard user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/classmates" element={
        <ProtectedRoute>
          {({ user }) => <ClassmatesPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/gifting" element={
        <ProtectedRoute>
          {({ user }) => <GiftingPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/savings-goals" element={
        <ProtectedRoute>
          {({ user }) => <SavingsGoalsPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/my-wallet" element={
        <ProtectedRoute>
          {({ user }) => <MyWalletPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute>
          {({ user }) => <ContentManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/store" element={
        <ProtectedRoute>
          {({ user }) => <AdminStoreManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/investments" element={
        <ProtectedRoute>
          {({ user }) => <AdminInvestmentManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/quests" element={
        <ProtectedRoute>
          {({ user }) => <AdminQuestsPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/parent/shopping-list" element={
        <ProtectedRoute>
          {({ user }) => <ParentShoppingList user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/garden" element={
        <ProtectedRoute>
          {({ user }) => <AdminGardenManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/stock-market" element={
        <ProtectedRoute>
          {({ user }) => <StockMarketPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/stocks" element={
        <ProtectedRoute>
          {({ user }) => <AdminStockManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/badges" element={
        <ProtectedRoute>
          {({ user }) => <AdminBadgeManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/video" element={
        <ProtectedRoute>
          {({ user }) => <AdminVideoManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/glossary" element={
        <ProtectedRoute>
          {({ user }) => <AdminGlossaryManagement user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/repository" element={
        <ProtectedRoute>
          {({ user }) => <AdminTeacherRepository user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/glossary" element={
        <ProtectedRoute>
          {({ user }) => <GlossaryPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          {({ user }) => <LiveClassesPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/live-classes" element={
        <ProtectedRoute>
          {({ user }) => <LiveClassesAdmin user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/activity-scores" element={
        <ProtectedRoute>
          {({ user }) => <ActivityScoresPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/activity-scores/:childId" element={
        <ProtectedRoute>
          {({ user }) => <ActivityScoresPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/activity-analytics/:contentId" element={
        <ProtectedRoute>
          {({ user }) => <TeacherActivityAnalytics user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute>
          {({ user }) => <AdminSubscriptionManagement user={user} />}
        </ProtectedRoute>
      } />
    </Routes>
      </Suspense>
    </>
  );
}

function App() {
  const { progress } = useUploadProgress();
  return (
    <div className="min-h-screen">
      <ContentProtection />
      {/* Toaster mounted before the router so it subscribes to the sonner
          store before any route's mount-time useEffect fires a toast
          (e.g. FinancialLiteracyPage's session_expired message) — sibling effects
          run in JSX order, so this must come first. */}
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <UploadProgressBar progress={progress} />
    </div>
  );
}

export default App;
